const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE',
  'P', 'DIV', 'BR', 'UL', 'OL', 'LI', 'SPAN',
  'IMG', 'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH'
]);

// Attributes allowed per tag — everything else is stripped. Keeps pasted images/tables
// (including from Word/Excel) usable while blocking event handlers and script/data-URI abuse.
const ALLOWED_ATTRS: Record<string, string[]> = {
  IMG: ['src', 'alt', 'width', 'height', 'data-inline-blob-path'],
  TD: ['colspan', 'rowspan'],
  TH: ['colspan', 'rowspan'],
};

const isSafeImgSrc = (value: string): boolean =>
  /^(data:image\/|https?:\/\/)/i.test(value.trim());

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeNode = (node: Node): void => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as HTMLElement;

    if (!ALLOWED_TAGS.has(element.tagName)) {
      const parent = element.parentNode;
      while (element.firstChild) {
        parent?.insertBefore(element.firstChild, element);
      }
      parent?.removeChild(element);
      return;
    }

    const allowedAttrs = ALLOWED_ATTRS[element.tagName] ?? [];
    Array.from(element.attributes).forEach(attr => {
      if (!allowedAttrs.includes(attr.name)) {
        element.removeAttribute(attr.name);
        return;
      }
      if (attr.name === 'src' && !isSafeImgSrc(attr.value)) {
        element.removeAttribute(attr.name);
      }
    });

    // An <img> with no usable src (stripped above, or never had one) renders as a broken icon — drop it.
    if (element.tagName === 'IMG' && !element.getAttribute('src') && !element.getAttribute('data-inline-blob-path')) {
      element.parentNode?.removeChild(element);
      return;
    }
  }

  Array.from(node.childNodes).forEach(sanitizeNode);
};

export const sanitizeRichText = (html: string): string => {
  if (!html || !html.trim()) return '';

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return escapeHtml(html).replace(/\n/g, '<br/>');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  Array.from(doc.body.childNodes).forEach(sanitizeNode);

  return doc.body.innerHTML;
};

export const stripRichText = (value: string): string => {
  if (!value) return '';

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(value, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

export const isRichTextEmpty = (value: string): boolean => {
  if (!value || !value.trim()) return true;

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return !stripRichText(value);
  }

  // An image or table carries meaningful content even with no visible text (e.g. a pasted
  // screenshot with no caption) — textContent alone would wrongly report these as "empty".
  const parser = new DOMParser();
  const doc = parser.parseFromString(value, 'text/html');
  if (doc.querySelector('img, table')) return false;

  return !stripRichText(value);
};

export const toEditorHtml = (value: string): string => {
  if (!value) return '';

  const looksLikeHtml = /<[^>]+>/.test(value);
  if (looksLikeHtml) {
    return sanitizeRichText(value);
  }

  return escapeHtml(value).replace(/\n/g, '<br/>');
};

export const renderRichText = (value: string): { __html: string } => {
  if (!value) return { __html: '' };

  return { __html: sanitizeRichText(toEditorHtml(value)) };
};
